import { useState } from 'react'
import React from 'react'
import PlannedExpertiseRequesterHelpDialog from '../PlannedExpertiseRequesterHelpDialog'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import IconButton from '@mui/material/IconButton'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Button from '@mui/material/Button'
import useStyles from '../_styles'
import { RequesterStepProps } from '../_props'
import { EmptySearchRequesterIcon, HelpExpertiseIcon, NoRequesterIcon } from '../../../../assets/icons/icons'
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded'
import useButtonStyles from '../../../../styles/buttonsStyles'
import { useNavigate } from 'react-router-dom'
import PlannedExpertiseAddRequesterDialog from '../PlannedExpertiseAddRequesterDialog'
import { useGetIdsHealthcareservicesHcsIdRequestersForAct } from '@toktokdoctech/tokapi-client'
import useCurrentHcs from '../../../../hooks/useCurrentHcs'
import { useDebouncedCallback } from '@workspace/frontend-kit'
import { normalizeRequesterLabel } from '../../../Patient/History/_utils'
import { Box, Stack } from '@mui/material'
import { defaultPalette } from '../../../../theme/idsTheme'

export default function RequesterStep({
    patient,
    consentGiven,
    onConsentChange,
    onDataChange,
    onSelectAndContinue,
}: RequesterStepProps & { onSelectAndContinue?: () => void }) {
    const navigate = useNavigate()
    const classes = useStyles()
    const buttons = useButtonStyles()
    const currentHcs = useCurrentHcs()
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
    const [selectedRequesterId, setSelectedRequesterId] = useState<string | null>(null)
    const [isRequesterHelpDialogOpen, setIsRequesterHelpDialogOpen] = useState(false)
    const [isAddRequesterAbandonDialogOpen, setIsAddRequesterAbandonDialogOpen] = useState(false)
    const [editRequesterAbandonDialogOpen, setEditRequesterAbandonDialogOpen] = useState(false)

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setDebouncedSearchTerm(value)
    }, 300)

    const { data: requestersData, isLoading } = useGetIdsHealthcareservicesHcsIdRequestersForAct(currentHcs.id, {
        search: debouncedSearchTerm || undefined,
        skip: 0,
        limit: 50,
    })

    const requesters = requestersData?.items ?? []
    const totalRequesters = requestersData?.total ?? 0
    const hasSearchTerm = debouncedSearchTerm.length > 0

    const handleSelectRequester = (requesterId: string) => {
        const selectedRequester = requesters.find((r) => r.id === requesterId)
        if (!selectedRequester) return

        setSelectedRequesterId(requesterId)
        onConsentChange?.(true)
        onDataChange?.({
            requesterId: selectedRequester.id,
            requiredExpertLabel: normalizeRequesterLabel(selectedRequester.fullName, selectedRequester.profession),
            profession: selectedRequester.profession,
            speciality: selectedRequester.speciality ?? '',
        })
        onSelectAndContinue?.()
    }

    const handleResetSearch = () => {
        setSearchTerm('')
        setDebouncedSearchTerm('')
        debouncedSearch('')
    }

    return (
        <>
            <div className={classes.stepContent}>
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                    <Typography className={classes.patientLabel}>Sélectionnez le requérant</Typography>
                    <Button
                        onClick={() => setIsAddRequesterAbandonDialogOpen(true)}
                        startIcon={<AddCircleRoundedIcon />}
                        className={buttons.ButtonBlack}
                        disableElevation
                        variant="contained"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        Ajouter
                    </Button>
                    <PlannedExpertiseAddRequesterDialog
                        isOpen={isAddRequesterAbandonDialogOpen}
                        onClose={() => setIsAddRequesterAbandonDialogOpen(false)}
                        onConfirm={() => navigate('/requester')}
                    />
                </div>

                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Rechercher un requérant"
                    value={searchTerm}
                    onChange={(e) => {
                        const value = e.target.value
                        setSearchTerm(value)
                        debouncedSearch(value)
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon className={classes.clearSearchIcon} />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleResetSearch}
                                    edge="end"
                                    aria-label="Effacer la recherche"
                                >
                                    <ClearIcon className={classes.clearSearchIcon} fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Paper className={classes.patientList}>
                    {isLoading && (
                        <div className={classes.noResults}>
                            <Typography>Chargement...</Typography>
                        </div>
                    )}

                    {!isLoading && hasSearchTerm && requesters.length > 0 && (
                        <div className={classes.resultsCount}>
                            <Typography variant="body2">
                                {requesters.length} résultat{requesters.length > 1 ? 's' : ''} pour "
                                {debouncedSearchTerm}"
                            </Typography>
                        </div>
                    )}

                    {!isLoading && !hasSearchTerm && totalRequesters === 0 ? (
                        <div className={classes.noResults}>
                            <NoRequesterIcon className={classes.searchRequesterIcon} />
                            <Typography className={classes.noPatient}>
                                Aucun requérant n’est enregistré pour le moment. Ajoutez le professionnel à l’origine de
                                la demande pour pouvoir poursuivre
                            </Typography>
                            <Button className={classes.helpButton} onClick={() => setIsRequesterHelpDialogOpen(true)}>
                                <HelpExpertiseIcon />
                                Aide : qu'est-ce qu'un requérant ?
                            </Button>
                            <PlannedExpertiseRequesterHelpDialog
                                isOpen={isRequesterHelpDialogOpen}
                                onClose={() => setIsRequesterHelpDialogOpen(false)}
                            />
                        </div>
                    ) : !isLoading && requesters.length > 0 ? (
                        <List>
                            {requesters.map((requester) => {
                                const shouldBlock = currentHcs?.isRequesterValidationEnabled && !requester.securedEmail
                                return (
                                    <React.Fragment key={requester.id}>
                                        <ListItem
                                            disablePadding
                                            sx={(theme) => ({ backgroundColor: theme.palette.lightGrey.cardBackground })}
                                        >
                                            <ListItemButton
                                                onClick={() => handleSelectRequester(requester.id)}
                                                disabled={shouldBlock}
                                                sx={{ '&.Mui-disabled': { opacity: 1 } }}
                                                className={`\
                        ${selectedRequesterId === requester.id ? classes.selectedPatient : ''}\
                      `}
                                            >
                                                <ListItemText
                                                    primary={normalizeRequesterLabel(requester.fullName)}
                                                    secondary={
                                                        requester.speciality && requester.speciality.trim().length > 0
                                                            ? `${requester.profession} - ${requester.speciality}`
                                                            : `${requester.profession}`
                                                    }
                                                    primaryTypographyProps={{
                                                        className: classes.patientName,
                                                        sx: { padding: 0, ...(shouldBlock && { color: '#8B8B8B' }) },
                                                    }}
                                                    secondaryTypographyProps={{
                                                        className: classes.patientInfo,
                                                        sx: { padding: 0, ...(shouldBlock && { color: '#8B8B8B' }) },
                                                    }}
                                                />

                                                {selectedRequesterId === requester.id && (
                                                    <CheckCircleIcon className={classes.checkIcon} />
                                                )}
                                            </ListItemButton>
                                            {shouldBlock && (
                                                <Stack sx={{ paddingRight: '1rem', paddingY: '0.875rem' }}>
                                                    <Stack gap="0.25rem">
                                                        <Stack
                                                            direction="row"
                                                            sx={{
                                                                gap: '0.5rem',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    width: '0.5rem',
                                                                    height: '0.5rem',
                                                                    backgroundColor: defaultPalette.orangeWarning.main,
                                                                    borderRadius: '9999px',
                                                                }}
                                                            />
                                                            <Typography color={defaultPalette.orangeWarning.main}>
                                                                Courriel manquant
                                                            </Typography>
                                                        </Stack>
                                                        <Button
                                                            variant="contained"
                                                            disableElevation
                                                            onClick={() => {
                                                                setSelectedRequesterId(requester.id)
                                                                setEditRequesterAbandonDialogOpen(true)
                                                            }}
                                                            sx={{
                                                                padding: '0.375rem 1.5rem',
                                                                borderRadius: '0.375rem',
                                                                backgroundColor: defaultPalette.orangeWarning.main,
                                                                '&:hover': {
                                                                    backgroundColor: defaultPalette.orangeWarning.main,
                                                                    transition: 'opacity 0.2s ease-out',
                                                                    opacity: 0.75,
                                                                },
                                                            }}
                                                        >
                                                            Renseigner
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                            )}
                                        </ListItem>
                                    </React.Fragment>
                                )
                            })}
                        </List>
                    ) : (
                        !isLoading && (
                            <div className={classes.noResults}>
                                <EmptySearchRequesterIcon className={classes.searchRequesterIcon} />
                                <Typography className={classes.noPatient}>
                                    Désolé, aucun requérant n’a été trouvé. Vérifiez l’orthographe du nom et du prénom,
                                    puis réessayez
                                </Typography>
                                <Button className={classes.resetFilter} onClick={handleResetSearch}>
                                    Réinitialiser la recherche
                                </Button>
                                <Button
                                    className={classes.helpButton}
                                    onClick={() => setIsRequesterHelpDialogOpen(true)}
                                >
                                    <HelpExpertiseIcon />
                                    Aide : qu'est-ce qu'un requérant ?
                                </Button>
                                <PlannedExpertiseRequesterHelpDialog
                                    isOpen={isRequesterHelpDialogOpen}
                                    onClose={() => setIsRequesterHelpDialogOpen(false)}
                                />
                            </div>
                        )
                    )}
                </Paper>
            </div>
            <PlannedExpertiseAddRequesterDialog
                isOpen={editRequesterAbandonDialogOpen}
                onClose={() => setEditRequesterAbandonDialogOpen(false)}
                onConfirm={() => navigate(`/requester/${selectedRequesterId}`)}
            />
        </>
    )
}
